/**
 * Word-level streaming pacer.
 *
 * Character-by-character reveal reads as a gimmick; word-by-word reads as
 * thinking. Cadence 28–45ms per word, jittered, per the Rammy motion spec.
 *
 * Incoming SSE deltas are buffered and released one word at a time. If the
 * model outruns the pacer the backlog is drained several words per tick so
 * the caption never falls meaningfully behind the audio.
 */
export function createWordPacer(onText, { min = 28, max = 45, catchUpAt = 240 } = {}) {
  let pending = "";
  let timer = null;
  let ended = false;
  let stopped = false;
  let onDrained = null;

  const delay = () => min + Math.random() * (max - min);

  const schedule = () => {
    if (timer || stopped) return;
    timer = setTimeout(tick, delay());
  };

  const takeWord = () => {
    const m = /^\s*\S+\s*/.exec(pending);
    if (!m) return null;
    // Mid-word and more text may still arrive — hold until the word closes.
    if (!ended && !/\s$/.test(m[0]) && m[0].length === pending.length) return null;
    pending = pending.slice(m[0].length);
    return m[0];
  };

  const tick = () => {
    timer = null;
    if (stopped) return;
    // Drain faster when we're behind.
    const budget = pending.length > catchUpAt ? 4 : 1;
    let out = "";
    for (let n = 0; n < budget; n += 1) {
      const w = takeWord();
      if (!w) break;
      out += w;
    }
    if (out) onText(out);
    if (pending) schedule();
    else if (ended && onDrained) {
      const cb = onDrained;
      onDrained = null;
      cb();
    }
  };

  return {
    push(text) {
      if (!text || stopped) return;
      pending += text;
      schedule();
    },
    /** No more input coming — drain what's left at the same cadence. */
    end(cb) {
      ended = true;
      onDrained = cb || null;
      if (!pending) {
        if (onDrained) {
          const done = onDrained;
          onDrained = null;
          done();
        }
        return;
      }
      schedule();
    },
    /** Barge-in / abort — emit everything left at once and stop. */
    flush() {
      if (stopped) return;
      if (timer) clearTimeout(timer);
      timer = null;
      const rest = pending;
      pending = "";
      stopped = true;
      if (rest) onText(rest);
    },
    cancel() {
      stopped = true;
      pending = "";
      if (timer) clearTimeout(timer);
      timer = null;
    },
  };
}
